// Utility functions for working with maps and routes

/**
 * Generates a static map URL using Google Maps Static API
 * 
 * @param origin Origin coordinates
 * @param destination Destination coordinates 
 * @param waypoints Optional array of waypoint coordinates
 * @param encodedPolyline Encoded polyline of the route
 * @param apiKey Google Maps API Key
 * @param width Image width (max 640 for free tier)
 * @param height Image height (max 640 for free tier)
 * @param scale Image scale (1 or 2, where 2 is for high DPI displays)
 * @returns URL string for the static map image
 */
export const generateStaticMapUrl = (
  origin: { lat: number; lng: number } | null,
  destination: { lat: number; lng: number } | null,
  waypoints: { lat: number; lng: number }[] = [],
  encodedPolyline?: string,
  apiKey: string = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  width: number = 640,
  height: number = 320,
  scale: number = 2
): string => {
  if (!origin || !destination || !apiKey) {
    console.warn('Missing required parameters for static map URL generation');
    return '';
  }
  
  // Start building the URL with API key and size parameters
  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap?';
  const params = new URLSearchParams();
  
  // Set API key
  params.append('key', apiKey);
  
  // Set map size and scale
  params.append('size', `${width}x${height}`);
  params.append('scale', `${scale}`);
  
  // Add origin marker (green)
  params.append('markers', `color:green|label:A|${origin.lat},${origin.lng}`);
  
  // Add destination marker (red)
  params.append('markers', `color:red|label:B|${destination.lat},${destination.lng}`);
  
  // Add waypoint markers (blue)
  waypoints.forEach((waypoint, index) => {
    // Use letters C, D, E, etc. for waypoints
    const label = String.fromCharCode(67 + index); // 67 is ASCII for 'C'
    params.append('markers', `color:blue|label:${label}|${waypoint.lat},${waypoint.lng}`);
  });
  
  // Add encoded polyline path if available
  if (encodedPolyline && encodedPolyline.trim() !== '') {
    params.append('path', `weight:5|color:0x0096FF|enc:${encodedPolyline}`);
  } else {
    // If no polyline, create a simple path between origin and destination
    params.append('path', `weight:5|color:0x0096FF|${origin.lat},${origin.lng}|${destination.lat},${destination.lng}`);
  }
  
  // Add map styling
  params.append('maptype', 'roadmap');
  
  // Ensure the map is centered properly by adding a center parameter
  // Calculate center between origin and destination
  const centerLat = (origin.lat + destination.lat) / 2;
  const centerLng = (origin.lng + destination.lng) / 2;
  params.append('center', `${centerLat},${centerLng}`);
  
  // Add an appropriate zoom level (automatic)
  // Alternatively, you can calculate this based on distance between points
  
  return baseUrl + params.toString();
};

/**
 * Creates a fallback static map URL when encoded polyline is not available
 */
export const generateSimpleStaticMapUrl = (
  origin: { lat: number; lng: number } | null,
  destination: { lat: number; lng: number } | null,
  waypoints: { lat: number; lng: number }[] = [],
  apiKey: string = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
  width: number = 640,
  height: number = 320
): string => {
  if (!origin || !destination || !apiKey) {
    return '';
  }
  
  // Calculate center point and appropriate zoom level
  // If there are waypoints, include them in the center calculation
  let centerLat = origin.lat;
  let centerLng = origin.lng;
  let pointCount = 1;
  
  if (destination) {
    centerLat += destination.lat;
    centerLng += destination.lng;
    pointCount++;
  }
  
  // Include waypoints in center calculation
  waypoints.forEach(waypoint => {
    centerLat += waypoint.lat;
    centerLng += waypoint.lng;
    pointCount++;
  });
  
  // Calculate average
  centerLat = centerLat / pointCount;
  centerLng = centerLng / pointCount;
  
  // Calculate rough zoom level based on distance
  const latDiff = Math.abs(destination.lat - origin.lat);
  const lngDiff = Math.abs(destination.lng - origin.lng);
  
  // Add waypoint differences to calculate total span
  let maxLatDiff = latDiff;
  let maxLngDiff = lngDiff;
  
  waypoints.forEach(waypoint => {
    maxLatDiff = Math.max(maxLatDiff, Math.abs(waypoint.lat - origin.lat), Math.abs(waypoint.lat - destination.lat));
    maxLngDiff = Math.max(maxLngDiff, Math.abs(waypoint.lng - origin.lng), Math.abs(waypoint.lng - destination.lng));
  });
  
  const maxDiff = Math.max(maxLatDiff, maxLngDiff);
  
  // Simple algorithm to determine zoom level
  // Lower value = more zoomed out
  let zoom = 10; // Default zoom level
  if (maxDiff > 10) zoom = 4;
  else if (maxDiff > 5) zoom = 5;
  else if (maxDiff > 3) zoom = 6;
  else if (maxDiff > 1) zoom = 7;
  else if (maxDiff > 0.5) zoom = 8;
  else if (maxDiff > 0.1) zoom = 9;
  
  // Build the URL
  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap?';
  const params = new URLSearchParams({
    center: `${centerLat.toFixed(6)},${centerLng.toFixed(6)}`,
    zoom: zoom.toString(), 
    size: `${width}x${height}`, 
    key: apiKey,
  });
  
  // Add origin marker
  params.append('markers', `color:green|label:A|${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}`);
  
  // Add destination marker
  params.append('markers', `color:red|label:B|${destination.lat.toFixed(6)},${destination.lng.toFixed(6)}`);
  
  // Add waypoint markers
  waypoints.forEach((waypoint, index) => {
    // Use letters C, D, E, etc. for waypoints
    const label = String.fromCharCode(67 + index); // 67 is ASCII for 'C'
    params.append('markers', `color:blue|label:${label}|${waypoint.lat.toFixed(6)},${waypoint.lng.toFixed(6)}`);
  });
  
  // Create a path between all points
  let pathPoints = `${origin.lat.toFixed(6)},${origin.lng.toFixed(6)}`;
  
  // Add waypoints to path
  waypoints.forEach(waypoint => {
    pathPoints += `|${waypoint.lat.toFixed(6)},${waypoint.lng.toFixed(6)}`;
  });
  
  // Add destination to path
  pathPoints += `|${destination.lat.toFixed(6)},${destination.lng.toFixed(6)}`;
  
  params.append('path', `color:0x0096FF|weight:5|${pathPoints}`);
  
  return baseUrl + params.toString();
};